import { Flex, Box, Divider, Text } from '@chakra-ui/react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createInterview, fetchApplicationList } from '../../../api/recruitment/recruitment'
import { fetchEmployeeList } from '../../../api/workforce/workforce'
import FormModal from '../../common/FormModal'
import { toLocalDateString } from '../../../helpers/dateUtils'
import { useFormMutation } from '../../../hooks/useFormMutation'
import ModalInput from '../../forms/modalForms/modalInput'
import ModalSelect from '../../forms/modalForms/modalSelect'
import ModalDateInput from '../../forms/modalForms/modalDateInput'
import ModalTimeInput from '../../forms/modalForms/modalTimeInput'

interface ScheduleInterviewModalProps {
  isOpen: boolean
  onClose: () => void
  applicationId?: string
}

const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({ isOpen, onClose, applicationId }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    application: applicationId || '',
    interviewer: '',
    date: null as Date | null,
    time: '',
    duration_minutes: '60',
    type: 'video' as 'phone' | 'video' | 'onsite',
    notes: '',
  })

  const [interviewerSearch, setInterviewerSearch] = useState('')
  const [applicationSearch, setApplicationSearch] = useState('')

  useEffect(() => {
    if (applicationId) {
      setFormData((prev) => ({ ...prev, application: applicationId }))
    }
  }, [applicationId])

  const applicationsQuery = useInfiniteQuery({
    queryKey: ['applications', 'schedule-interview', applicationSearch],
    queryFn: ({ pageParam }) =>
      fetchApplicationList({
        search: applicationSearch,
        page: pageParam,
        pageSize: 20,
        filters: {},
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.has_next ? (lastPage?.page ?? 0) + 1 : undefined),
    enabled: isOpen,
  })

  const applicationsList = useMemo(() => {
    const pages = applicationsQuery.data?.pages ?? []
    return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
  }, [applicationsQuery.data?.pages])

  const applicationOptions = useMemo(
    () =>
      applicationsList.map((app: any) => {
        const candidate = typeof app.candidate === 'object' ? app.candidate : null
        const vacancy = typeof app.vacancy === 'object' ? app.vacancy : null
        const candidateName = candidate ? `${candidate.first_name} ${candidate.last_name}` : ''
        const vacancyTitle = vacancy?.title ?? 'N/A'
        return {
          value: String(app.id),
          label: `${candidateName} – ${vacancyTitle}`,
        }
      }),
    [applicationsList]
  )

  const interviewersQuery = useInfiniteQuery({
    queryKey: ['interviewers', interviewerSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: interviewerSearch,
        page: pageParam,
        pageSize: 20,
        filters: {},
        minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.has_next ? (lastPage?.page ?? 0) + 1 : undefined),
    enabled: isOpen,
  })

  const interviewersList = useMemo(() => {
    const pages = interviewersQuery.data?.pages ?? []
    return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
  }, [interviewersQuery.data?.pages])

  const interviewerOptions = useMemo(
    () =>
      interviewersList.map((emp: any) => ({
        value: String(emp.id),
        label: `${emp.first_name} ${emp.last_name}`,
        profilePicture: emp.profile_picture ?? '',
      })),
    [interviewersList]
  )

  const handleClose = () => {
    setFormData({
      application: applicationId || '',
      interviewer: '',
      date: null,
      time: '',
      duration_minutes: '60',
      type: 'video',
      notes: '',
    })
    setInterviewerSearch('')
    setApplicationSearch('')
    onClose()
  }

  const mutation = useFormMutation({
    mutationFn: createInterview,
    queryKey: 'interviews',
    successMessage: t('recruitment.modals.scheduleInterview.successMessage'),
    onClose: handleClose,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.invalidateQueries({ queryKey: ['candidates'] })
    },
  })

  const handleSubmit = () => {
    if (!formData.application || !formData.date || !formData.time) return

    // Combine date + time into ISO datetime
    const dateISO = toLocalDateString(formData.date)
    const scheduled_at = `${dateISO}T${formData.time}:00`

    mutation.mutate({
      application: formData.application,
      interviewer: formData.interviewer || undefined,
      scheduled_at,
      duration_minutes: parseInt(formData.duration_minutes, 10),
      type: formData.type,
      notes: formData.notes,
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const durationOptions = [
    { value: '15', label: t('recruitment.modals.scheduleInterview.durationOptions.15min') },
    { value: '30', label: t('recruitment.modals.scheduleInterview.durationOptions.30min') },
    { value: '45', label: t('recruitment.modals.scheduleInterview.durationOptions.45min') },
    { value: '60', label: t('recruitment.modals.scheduleInterview.durationOptions.60min') },
    { value: '90', label: t('recruitment.modals.scheduleInterview.durationOptions.90min') },
    { value: '120', label: t('recruitment.modals.scheduleInterview.durationOptions.120min') },
  ]

  const typeOptions = [
    { value: 'phone', label: t('recruitment.modals.scheduleInterview.types.phone') },
    { value: 'video', label: t('recruitment.modals.scheduleInterview.types.video') },
    { value: 'onsite', label: t('recruitment.modals.scheduleInterview.types.onsite') },
  ]

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('recruitment.modals.scheduleInterview.title')}
      onSubmit={handleSubmit}
      isLoading={mutation.isPending}
      submitLabel={t('recruitment.modals.scheduleInterview.scheduleButton')}
    >
      <Text textStyle="h-xs" color="solid.700" mb="2px">
        Application
      </Text>

      <ModalSelect
        label={t('recruitment.modals.scheduleInterview.application')}
        name="application"
        value={formData.application}
        options={applicationOptions}
        onChange={handleChange}
        placeholder={t('recruitment.modals.scheduleInterview.selectApplication')}
        onSearch={setApplicationSearch}
        onLoadMore={() => applicationsQuery.fetchNextPage()}
        hasMore={applicationsQuery.hasNextPage}
        isLoadingMore={applicationsQuery.isFetchingNextPage}
        isLoading={applicationsQuery.isLoading}
        required
      />
      <ModalSelect
        label={t('recruitment.modals.scheduleInterview.interviewer')}
        name="interviewer"
        value={formData.interviewer}
        options={interviewerOptions}
        onChange={handleChange}
        placeholder={t('recruitment.modals.scheduleInterview.selectInterviewer')}
        hasProfilePicture
        onSearch={setInterviewerSearch}
        onLoadMore={() => interviewersQuery.fetchNextPage()}
        hasMore={interviewersQuery.hasNextPage}
        isLoadingMore={interviewersQuery.isFetchingNextPage}
        isLoading={interviewersQuery.isLoading}
      />

      <Divider borderColor="solid.50" />

      <Text textStyle="h-xs" color="solid.700" mb="2px">
        Schedule
      </Text>

      <Flex gap="16px" width="100%">
        <Box flex="1">
          <ModalDateInput
            label={t('recruitment.modals.scheduleInterview.date', 'Date')}
            value={formData.date}
            onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
            placeholder="Select date"
            required
          />
        </Box>
        <Box flex="1">
          <ModalTimeInput
            label={t('recruitment.modals.scheduleInterview.time', 'Time')}
            name="time"
            value={formData.time}
            onChange={handleChange}
            required
          />
        </Box>
      </Flex>

      <Flex gap="16px" width="100%">
        <Box flex="1">
          <ModalSelect
            label={t('recruitment.modals.scheduleInterview.duration')}
            name="duration_minutes"
            value={formData.duration_minutes}
            options={durationOptions}
            onChange={handleChange}
            searchDisabled
          />
        </Box>
        <Box flex="1">
          <ModalSelect
            label={t('recruitment.modals.scheduleInterview.type')}
            name="type"
            value={formData.type}
            options={typeOptions}
            onChange={handleChange}
            searchDisabled
          />
        </Box>
      </Flex>

      <ModalInput
        label={t('recruitment.modals.scheduleInterview.notes')}
        type="textarea"
        name="notes"
        height="80px"
        placeholder={t('recruitment.modals.scheduleInterview.notesPlaceholder')}
        value={formData.notes}
        onChange={handleChange}
      />
    </FormModal>
  )
}

export default ScheduleInterviewModal
